import { useQuery } from '@apollo/client/react'
import {
  DataTable,
  FailureNotice,
  FilterBar,
  FOCUS_RING,
  Icon,
  SearchField,
  Section,
  Select,
  buttonClasses,
  cn,
  usePageTitle,
  useTranslate,
  type DataTableColumn,
} from '@athena/ui'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import {
  ManagedSubjectsDocument,
  type ManagedSubjectFieldsFragment,
} from '../graphql/generated/operations.js'
import { SubjectStatusTag } from '../features/display/Tags.js'
import { ListMessage, PageControls } from '../features/management/ManagementSupport.js'
import { countOf } from '../lib/display.js'
import {
  LIST_PAGE_SIZE,
  isFiltered,
  nextSearch,
  type SubjectSearch,
  type SearchChange,
} from '../lib/list-search.js'

/**
 * The subject catalog, the same way the account list is laid out: one row a
 * subject, the code as its link, and state and usage in columns. A subject is
 * less to read than an account, so the table is three columns and nothing is
 * set aside at tablet width.
 */
export function SubjectsScreen() {
  const t = useTranslate()
  usePageTitle(t('management.subjects'))
  const { language } = useParams({ from: '/$language' })
  const search = useSearch({ from: '/$language/subjects' })
  const navigate = useNavigate()
  const page = search.page ?? 1
  const [draft, setDraft] = useState(search.q ?? '')
  useEffect(() => setDraft(search.q ?? ''), [search.q])

  const { data, previousData, loading, error, refetch } = useQuery(ManagedSubjectsDocument, {
    variables: {
      input: {
        query: search.q ?? '',
        offset: (page - 1) * LIST_PAGE_SIZE,
        limit: LIST_PAGE_SIZE,
        active: search.status ? search.status === 'active' : null,
        sort: search.sort === 'newest' ? 'NEWEST' : 'CODE',
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })
  const result = (data ?? previousData)?.adminSubjectPage
  const filtered = isFiltered(search)

  function go(change: SearchChange<SubjectSearch>) {
    void navigate({
      to: '/$language/subjects',
      params: { language },
      search: nextSearch(search, change),
    })
  }

  const columns: DataTableColumn<ManagedSubjectFieldsFragment>[] = [
    {
      id: 'subject',
      header: t('admin.columnSubject'),
      className: 'md:w-1/2',
      cell: (subject) => (
        <>
          <Link
            to="/$language/subjects/$subjectId"
            params={{ language, subjectId: subject.id }}
            className={cn(
              'rounded-sm font-semibold text-text underline-offset-2 hover:text-primary hover:underline',
              FOCUS_RING,
            )}
          >
            {language === 'en' ? subject.englishName : subject.spanishName}
          </Link>
          <span className="block text-text-secondary">
            {subject.code} · {language === 'en' ? subject.spanishName : subject.englishName}
          </span>
        </>
      ),
    },
    {
      id: 'status',
      header: t('admin.columnStatus'),
      cell: (subject) => <SubjectStatusTag active={subject.active} />,
    },
    {
      id: 'practices',
      header: t('admin.columnPractices'),
      align: 'end',
      className: 'tabular-nums',
      cell: (subject) =>
        countOf(t, subject.practiceCount, 'management.usageOne', 'management.usageOther'),
    },
  ]

  return (
    <Section
      id="subjects"
      level={1}
      title={t('management.subjects')}
      aside={
        <Link
          to="/$language/subjects/new"
          params={{ language }}
          className={cn(buttonClasses({ size: 'sm' }), 'gap-1.5 max-md:h-touch-target-min')}
        >
          <Icon.Plus aria-hidden="true" className="size-icon-sm" />
          {t('management.createSubject')}
        </Link>
      }
    >
      <form
        role="search"
        aria-label={t('management.subjects')}
        onSubmit={(event) => {
          event.preventDefault()
          const q = draft.trim()
          go({ q: q === '' ? undefined : q })
        }}
      >
        <FilterBar
          density="compact"
          clearLabel={t('common.clearFilters')}
          {...(filtered ? { onClear: () => go({ q: undefined, status: undefined }) } : {})}
          lead={
            <SearchField
              name="q"
              label={t('admin.searchSubjects')}
              placeholder={t('admin.searchSubjects')}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          }
        >
          <Select
            label={t('admin.columnStatus')}
            value={search.status ?? 'all'}
            onValueChange={(value) =>
              go({ status: value === 'all' ? undefined : (value as SubjectSearch['status']) })
            }
            options={[
              { value: 'all', label: t('admin.allStatuses') },
              { value: 'active', label: t('admin.subjectStatusActive') },
              { value: 'inactive', label: t('admin.subjectStatusInactive') },
            ]}
          />
          <Select
            label={t('management.sort')}
            value={search.sort ?? 'code'}
            onValueChange={(value) =>
              go({ sort: value === 'code' ? undefined : (value as SubjectSearch['sort']) })
            }
            options={[
              { value: 'code', label: t('management.code') },
              { value: 'newest', label: t('management.newest') },
            ]}
          />
        </FilterBar>
      </form>

      {error ? (
        <FailureNotice
          title={t('admin.listLoadError')}
          error={error}
          retrying={loading}
          onRetry={() => void refetch()}
        />
      ) : null}

      {result ? (
        <p aria-live="polite" className="text-body-sm text-text-secondary">
          {countOf(t, result.total, 'admin.subjectsCountOne', 'admin.subjectsCountOther')}
        </p>
      ) : null}

      {result && result.total === 0 ? (
        <ListMessage
          action={
            filtered
              ? {
                  label: t('common.clearFilters'),
                  onClick: () => go({ q: undefined, status: undefined }),
                }
              : undefined
          }
        >
          {filtered ? t('admin.noMatchingSubjects') : t('admin.noSubjects')}
        </ListMessage>
      ) : result || (loading && !error) ? (
        <DataTable
          caption={t('admin.subjectsCaption')}
          columns={columns}
          rows={result?.items ?? []}
          rowKey={(subject) => subject.id}
          rowTone={(subject) => (subject.active ? 'default' : 'muted')}
          busy={loading && Boolean(result)}
          loadingRows={6}
        />
      ) : null}

      {result ? (
        <PageControls
          page={page}
          total={result.total}
          limit={LIST_PAGE_SIZE}
          disabled={loading}
          onChange={(next) => go({ page: next })}
        />
      ) : null}
    </Section>
  )
}
